import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { graphql } from 'gql';
import { CharacterWhere, EpisodeWhere, InputMaybe, SeasonWhere, SortDirection } from 'gql/graphql';
import type { AppThunk, RootState } from '../../../store';
import areArraysEqual from '../../../util/areArraysEqual';
import isPluralCharacter from '../../../util/isPluralCharacter';
import { Filter } from '../types';
import { setCharacterOptions, setEpisodeOptions, setFilter, setSeasonOptions } from './actions';

export const updateSessionAndFilter = (showId: string, filter: Filter): AppThunk => (dispatch, getState) => {
    const stateSeasons = getState().recordingSession.draftSession.filter.seasons;
    const stateEpisodes = getState().recordingSession.draftSession.filter.episodes;
    const stateCharacters = getState().recordingSession.draftSession.filter.characters;
    const stateSeasonOptions = getState().recordingSession.seasonOptions.options;
    const stateEpisodeOptions = getState().recordingSession.episodeOptions.options;
    const stateCharacterOptions = getState().recordingSession.characterOptions.options;

    if (!areArraysEqual(filter.seasons, stateSeasons)
        || !areArraysEqual(filter.characters, stateCharacters)
        || !areArraysEqual(filter.episodes, stateEpisodes)
        || Object.keys(stateSeasonOptions).length === 0
        || Object.keys(stateEpisodeOptions).length === 0
        || Object.keys(stateCharacterOptions).length === 0
    ) {
        dispatch(setFilter(filter));
        dispatch(getSeasonOptions(showId));
        dispatch(getEpisodeOptions(showId));
        dispatch(getCharacterOptions(showId));
    }
}

export const getSeasonOptions = createAsyncThunk(
    'recordingSession/getSeasonOptions',
    async (showId: string, thunkAPI) => {
        const client = window.apolloClient as ApolloClient<NormalizedCacheObject>;
        const state = thunkAPI.getState() as RootState;

        client.watchQuery({
            query: GetSeasonOptions,
            variables: {
                where: getSessionOptionsWhere(showId, state.recordingSession.draftSession.filter),
            },
            fetchPolicy: 'cache-and-network',
        }).subscribe({
            next: (data) => thunkAPI.dispatch(setSeasonOptions(
                data.data.seasons.reduce((acc: Record<string, string>, season: {id: string, title: string}) => {
                    acc[season.id] = season.title;
                    return acc;
                }, {} as Record<string, string>)
            )),
            error(errorValue: never) {
                console.error(errorValue);
                throw errorValue;
            },
        })
    }
);

export const getEpisodeOptions = createAsyncThunk(
    'recordingSession/getEpisodeOptions',
    async (showId: string, thunkAPI) => {
        const client = window.apolloClient as ApolloClient<NormalizedCacheObject>;
        const state = thunkAPI.getState() as RootState;

        client.watchQuery({
            query: GetEpisodeOptions,
            variables: {
                where: getEpisodeOptionsWhere(showId, state.recordingSession.draftSession.filter),
            },
            fetchPolicy: 'cache-and-network',
        }).subscribe({
            next: (data) => thunkAPI.dispatch(setEpisodeOptions(
                data.data.episodes.reduce((acc: Record<string, string>, episode: {id: string, title: string}) => {
                    acc[episode.id] = episode.title;
                    return acc;
                }, {} as Record<string, string>)
            )),
            error(errorValue: never) {
                console.error(errorValue);
                throw errorValue;
            },
        })
    }
);

export const getCharacterOptions = createAsyncThunk(
    'recordingSession/getCharacterOptions',
    async (showId: string, thunkAPI) => {
        const client = window.apolloClient as ApolloClient<NormalizedCacheObject>;
        const state = thunkAPI.getState() as RootState;

        client.watchQuery({
            query: GetCharacterOptions,
            variables: {
                where: getCharacterOptionsWhere(showId, state.recordingSession.draftSession.filter),
                options: {
                    sort: [{ 'name': SortDirection.Asc }],
                },
            },
            fetchPolicy: 'cache-and-network',
        }).subscribe({
            next: (data) => thunkAPI.dispatch(setCharacterOptions(
                data.data.characters
                    .filter(c => !isPluralCharacter(c.name))
                    .reduce((acc: Record<string, string>, character: {id: string, name: string}) => {
                        acc[character.id] = character.name;
                        return acc;
                    }, {} as Record<string, string>)
            )),
            error(errorValue: never) {
                console.error(errorValue);
                throw errorValue;
            },
        })
    }
);

enum OptionKey {
    'seasonOptions',
    'episodeOptions',
    'characterOptions',
}

export const pendingActions = [
    [getSeasonOptions.pending, 'seasonOptions'] as [typeof getSeasonOptions.pending, keyof typeof OptionKey],
    [getEpisodeOptions.pending, 'episodeOptions'] as [typeof getEpisodeOptions.pending, keyof typeof OptionKey],
    [getCharacterOptions.pending, 'characterOptions'] as [typeof getCharacterOptions.pending, keyof typeof OptionKey],
];

export const rejectedActions = [
    [getSeasonOptions.rejected, 'seasonOptions'] as [typeof getSeasonOptions.rejected, keyof typeof OptionKey],
    [getEpisodeOptions.rejected, 'episodeOptions'] as [typeof getEpisodeOptions.rejected, keyof typeof OptionKey],
    [getCharacterOptions.rejected, 'characterOptions'] as [typeof getCharacterOptions.rejected, keyof typeof OptionKey],
];

const GetSeasonOptions = graphql(`
    query GetSeasonOptions($where: SeasonWhere) {
        seasons(where: $where) {
            id
            title
        }
    }`);

function getSessionOptionsWhere(showId: string, filter: Filter) {
    let where = {
        show: {
            id: showId,
        },
    } as InputMaybe<SeasonWhere>;

    if (filter.episodes && filter.episodes.length > 0) {
        where = {
            ...where,
            episodes_SOME: {
                id_IN: filter.episodes,
            },
        }
    }

    if (filter.characters && filter.characters.length > 0) {
        where = {
            ...where,
            episodes_SOME: {
                ...where?.episodes_SOME,
                characters_SOME: {
                    id_IN: filter.characters,
                },
            },
        }
    }

    return where;
}

const GetEpisodeOptions = graphql(`
    query GetEpisodeOptions($where: EpisodeWhere) {
        episodes(where: $where) {
            id
            title
        }
    }`);

function getEpisodeOptionsWhere(showId: string, filter: Filter) {
    let where = {
        season: {
            show: {
                id: showId,
            },
        },
    } as InputMaybe<EpisodeWhere>;

    if (filter.seasons && filter.seasons.length > 0) {
        where = {
            ...where,
            season: {
                ...where?.season,
                id_IN: filter.seasons,
            },
        }
    }

    if (filter.characters && filter.characters.length > 0) {
        where = {
            ...where,
            characters_SOME: {
                id_IN: filter.characters,
            },
        }
    }

    return where;
}

const GetCharacterOptions = graphql(`
    query GetCharacterOptions($where: CharacterWhere, $options: CharacterOptions) {
        characters(where: $where, options: $options) {
            id
            name
        }
    }`);

function getCharacterOptionsWhere(showId: string, filter: Filter) {
    let where = {
        show: {
            id: showId,
        },
    } as InputMaybe<CharacterWhere>;

    if (filter.seasons && filter.seasons.length > 0) {
        where = {
            ...where,
            episodes_SOME: {
                ...where?.episodes_SOME,
                season: {
                    ...where?.episodes_SOME?.season,
                    id_IN: filter.seasons,
                },
            },
        }
    }

    if (filter.episodes && filter.episodes.length > 0) {
        where = {
            ...where,
            episodes_SOME: {
                ...where?.episodes_SOME,
                id_IN: filter.episodes,
            },
        }
    }

    return where;
}